import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material'

interface Feature {
  id: string
  title: string
  tagline: string
  description: string
  highlights: string[]
  image: string
  cardImage: string
  heroImage: string
  midImage: string
}

interface FeatureSectionProps {
  feature: Feature
  imagePosition: 'left' | 'right'
  isEven: boolean
}

export function FeatureSection({ 
  feature, 
  imagePosition = 'left',
  isEven = false 
}: FeatureSectionProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  const imageContent = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src={feature.heroImage || feature.image}
        alt={feature.title}
        sx={{
          width: '100%',
          maxWidth: 660,  // Increased from 600 (10% larger)
          height: 'auto',
          objectFit: 'contain',
          borderRadius: 2,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 32px rgba(0,0,0,0.4)'
            : '0 8px 32px rgba(0,0,0,0.1)',
        }}
      />
    </Box>
  )
  
  const textContent = (
    <Box sx={{ py: 2 }}>
      {/* H2: Main Title */}
      <Typography
        variant="h2"
        component="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          color: theme.palette.text.primary,
          mb: 1.5,
          lineHeight: 1.2,
        }}
      >
        {feature.title}
      </Typography>
      
      {/* H3: Tagline/Subtitle */}
      <Typography
        variant="h3"
        component="h3"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
          color: theme.palette.text.secondary,
          mb: 3,
          lineHeight: 1.4,
        }}
      >
        {feature.tagline}
      </Typography>
      
      {/* HIGHLIGHTS: Display as Chips using theme colors */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5, 
          mb: 4,
        }}
      >
        {feature.highlights.map((highlight, index) => (
          <Chip
            key={index}
            label={highlight}
            size="small"
            sx={{
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.primary.main  // #7c9ffc in dark mode
                : theme.palette.primary.light, // #dae8ff in light mode
              color: theme.palette.mode === 'dark'
                ? theme.palette.primary.contrastText  // #32373f in dark mode
                : theme.palette.primary.contrastText,  // #32373f in light mode
              fontWeight: 500,
              fontSize: '0.85rem',
              borderRadius: 1.5,
              px: 0.5,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? theme.palette.primary.main  // #a7c2fd in dark mode
                  : theme.palette.primary.main, // #3560c1 in light mode
                color: theme.palette.mode === 'dark'
                  ? theme.palette.primary.contrastText
                  : '#fff',
              },
            }}
          />
        ))}
      </Box>
      
      {/* DESCRIPTION: Full text */}
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.8,
          fontSize: { xs: '1rem', md: '1.05rem' },
          maxWidth: 600,
        }}
      >
        {feature.description}
      </Typography>
    </Box>
  )
  
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 4, md: 8 },
        py: 4,
        px: { xs: 2, sm: 4, md: 6, lg: 8 },
        backgroundColor: isEven 
          ? 'background.paper'
          : 'background.papershade',
      }}
    >
      {/* Mobile: Always image first */}
      {isMobile ? (
        <>
          {imageContent}
          {textContent}
        </>
      ) : (
        /* Desktop: Alternating based on imagePosition */
        <>
          {imagePosition === 'left' ? (
            <>{imageContent}{textContent}</>
          ) : (
            <>{textContent}{imageContent}</>
          )}
        </>
      )}
    </Box>
  )
}
